import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBack } from '../hooks';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { C, cs } from '../styles/common';
import api from '../api';
import SharedMouldForm from '../components/forms/MouldForm';
import { LogForm } from './History';
import { HistoryLink } from '../components/LastChanged';
import { filled } from '../numberFormat';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

const TRIGGER_LABELS = {
  after_mould_close:  'After mould close',
  after_injection:    'After injection',
  after_cooling:      'After cooling',
  after_mould_open:   'After mould open',
  after_ejection_fwd: 'After ejection fwd',
  after_ejection_ret: 'After ejection return',
};

// Q_min [L/min] for turbulent flow (Re > 4000) at ~26°C water
const Q_MIN_FACTOR = 0.1621;

async function uploadPhoto(file, mouldId) {
  const ext  = file.name.split('.').pop();
  const path = `mould-${mouldId}-${Date.now()}.${ext}`;
  const res  = await fetch(
    `${SUPABASE_URL}/storage/v1/object/mould-photos/${path}`,
    { method: 'POST', headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` }, body: file }
  );
  if (!res.ok) throw new Error('Upload failed');
  return `${SUPABASE_URL}/storage/v1/object/public/mould-photos/${path}`;
}

export default function Moulds() {
  const navigate = useNavigate();
  const goBack   = useBack('/admin');
  const { isDirector } = useAuth();
  const [moulds, setMoulds]                         = useState([]);
  const [loading, setLoading]                       = useState(true);
  const [selected, setSelected]                     = useState(null);
  const [editing, setEditing]                       = useState(null);
  const [showDecommissioned, setShowDecommissioned] = useState(false);

  function load() {
    api.get('/api/v1/moulds')
      .then(res => { setMoulds(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  if (loading) return <div style={s.center}>Loading moulds...</div>;

  if (editing !== null) return (
    <div style={s.page}>
      <SharedMouldForm
        mould={editing === 'new' ? null : editing}
        isDirector={isDirector()}
        onSaved={() => { setEditing(null); setSelected(null); load(); }}
        onCancel={() => setEditing(null)}
      />
    </div>
  );

  if (selected) return (
    <MouldDetail
      mould={selected}
      isDirector={isDirector()}
      onEdit={() => setEditing(selected)}
      onBack={() => setSelected(null)}
      onDeleted={() => { setSelected(null); load(); }}
      onUpdated={updated => { setSelected(updated); load(); }}
    />
  );

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <button onClick={goBack} style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '12px', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
              <i className="fa-solid fa-chevron-left" style={{ fontSize: '10px' }} /> Back
            </button>
            <span style={{ color: '#2a3040' }}>·</span>
            <span style={{ fontSize: '12px', color: '#f0f2f5' }}>Moulds</span>
          </div>
          <h1 style={s.title}>Moulds</h1>
          <p style={s.sub}>{moulds.filter(m => m.status !== 'archived').length} active · {moulds.filter(m => m.status === 'archived').length} decommissioned</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <HistoryLink type="mould" />
          <button
            onClick={() => setShowDecommissioned(v => !v)}
            style={{ display: 'flex', alignItems: 'center', gap: '6px',
              background: showDecommissioned ? '#1f2937' : 'none',
              color: showDecommissioned ? '#9ca3af' : '#6b7280',
              border: `1px solid ${showDecommissioned ? '#4b5563' : '#2a3040'}`,
              borderRadius: '6px', padding: '6px 12px', fontSize: '11px',
              cursor: 'pointer', fontWeight: '600', fontFamily: 'system-ui, sans-serif' }}>
            <i className={`fa-solid fa-eye${showDecommissioned ? '' : '-slash'}`} style={{ fontSize: '10px' }} />
            {showDecommissioned ? 'Showing decommissioned' : 'Decommissioned hidden'}
          </button>
          {isDirector() && (
            <>
              <button style={s.importBtn} onClick={() => navigate('/admin/moulds/import')}>
                <i className="fa-solid fa-file-import" style={{ marginRight: '6px' }} />Import
              </button>
              <button style={s.addBtn} onClick={() => setEditing('new')}>+ Add Mould</button>
            </>
          )}
        </div>
      </div>
      {moulds.filter(m => showDecommissioned || m.status !== 'archived').length === 0 && (
        <div style={{ textAlign: 'center', color: '#6b7280', fontSize: '13px', padding: '64px 0' }}>
          No moulds yet. Use "Add Mould" or "Import" to get started.
        </div>
      )}
      <div style={s.grid}>
        {moulds.filter(m => showDecommissioned || m.status !== 'archived').map(mould => (
          <div key={mould.id} style={s.card} onClick={() => setSelected(mould)}>
            <div style={s.cardHeader}>
              <span style={s.ref}>{mould.ref}</span>
              <span style={s.family}>
                {mould.mat_families?.length > 0
                  ? mould.mat_families.join(' · ')
                  : (mould.mat_family || '')}
              </span>
            </div>
            <div style={s.cardBody}>
              <div style={s.mouldName}>{mould.name}</div>
              <div style={s.customer}>{mould.customer}</div>
              <div style={s.specs}>
                <Spec label="Cavities" value={mould.cavities} />
                <Spec label="Gate"     value={mould.gate_type} />
                <Spec label="Shot wt"  value={`${mould.sw_g}g`} />
                <Spec label="Part wt"  value={`${mould.pw_g}g`} />
                <Spec label="PA"       value={`${mould.pa_cm2}cm²`} />
                <Spec label="Wall max" value={`${mould.wmax_mm}mm`} />
              </div>
            </div>
            <div style={s.cardFooter}>
              <span style={{
                ...s.statusChip,
                background: mould.status === 'active' ? '#166534' : mould.status === 'maintenance' ? '#78350f' : mould.status === 'archived' ? '#1f2937' : '#7f1d1d',
                color: mould.status === 'active' ? '#86efac' : mould.status === 'maintenance' ? '#fde68a' : mould.status === 'archived' ? '#9ca3af' : '#fca5a5',
              }}>
                {mould.status === 'archived' ? 'Decommissioned' : mould.status}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── MOULD DETAIL ──────────────────────────────────────────────
function MouldDetail({ mould, isDirector, onEdit, onBack, onDeleted, onUpdated }) {
  const { toast, confirm } = useToast();
  // A setter logs the services they do (the director, 3 Nov 2026).
  const canLog = useAuth().can('log_mould_service');
  const [logging, setLogging] = useState(false);
  const [deleting, setDeleting]           = useState(false);
  const [deleteError, setDeleteError]     = useState('');
  const [decommissioning, setDecommissioning] = useState(false);
  const [uploading, setUploading]         = useState(false);
  const fileRef = useRef();

  function deleteMould() {
    confirm({
      message: `Delete ${mould.ref}? This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: () => {
        setDeleting(true);
        setDeleteError('');
        api.delete(`/api/v1/moulds/${mould.id}`)
          .then(() => onDeleted())
          .catch(err => {
            setDeleteError(err.response?.data?.error || 'Delete failed — try again');
            setDeleting(false);
          });
      },
    });
  }

  function decommissionMould() {
    confirm({
      message: `Decommission ${mould.ref}? It will no longer appear in scheduling but can be recommissioned later.`,
      confirmLabel: 'Decommission',
      danger: true,
      onConfirm: () => {
        setDecommissioning(true);
        api.put(`/api/v1/moulds/${mould.id}`, { status: 'archived' })
          .then(res => { onUpdated(res.data); })
          .catch(() => toast.error('Failed to decommission mould'))
          .finally(() => setDecommissioning(false));
      },
    });
  }

  function recommissionMould() {
    confirm({
      message: `Recommission ${mould.ref}? It will be marked as Active again.`,
      confirmLabel: 'Recommission',
      onConfirm: () => {
        setDecommissioning(true);
        api.put(`/api/v1/moulds/${mould.id}`, { status: 'active' })
          .then(res => { onUpdated(res.data); })
          .catch(() => toast.error('Failed to recommission mould'))
          .finally(() => setDecommissioning(false));
      },
    });
  }

  async function handlePhotoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadPhoto(file, mould.id);
      const res = await api.put(`/api/v1/moulds/${mould.id}`, { photo_url: url });
      onUpdated(res.data);
    } catch { toast.error('Photo upload failed'); }
    setUploading(false);
  }

  const gateDesc = mould.gate_type === 'edge' || mould.gate_type === 'submarine'
    ? `${mould.gate_type} — ${mould.gw_mm}×${mould.gd_mm}mm`
    : mould.gate_type === 'pin' || mould.gate_type === 'valve'
    ? `${mould.gate_type} — ⌀${mould.gdia_mm}mm`
    : mould.gate_type || '—';

  const cores      = mould.cores         || [];
  const specMech   = mould.special_mechanisms || {};
  const hrData     = mould.hot_runner_data    || {};
  const custFields = mould.custom_fields      || [];

  const hasSpecMech   = specMech.pneumatic_ejector || specMech.collapsible_cores || specMech.unscrewing;
  const hasCoolingCalc = mould.water_dia_mm || mould.target_mould_temp != null;

  return (
    <div style={s.page}>
      <div style={s.detailHeader}>
        <button style={s.backBtn} onClick={onBack}>← Back</button>
        {isDirector && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
            <div style={s.detailActions}>
              <button style={s.editBtn} onClick={onEdit}>Edit</button>
              {mould.status === 'archived'
                ? <button style={{ background: '#166534', color: '#86efac', border: 'none', borderRadius: '8px', padding: '10px 16px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}
                    onClick={recommissionMould} disabled={decommissioning}>
                    {decommissioning ? 'Updating...' : 'Recommission'}
                  </button>
                : <button style={{ background: '#374151', color: '#9ca3af', border: '1px solid #4b5563', borderRadius: '8px', padding: '10px 16px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}
                    onClick={decommissionMould} disabled={decommissioning}>
                    {decommissioning ? 'Updating...' : 'Decommission'}
                  </button>
              }
              <button style={s.deleteBtn} onClick={deleteMould} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
            {deleteError && (
              <div style={{ fontSize: '12px', color: '#fca5a5', maxWidth: '320px', textAlign: 'right' }}>{deleteError}</div>
            )}
          </div>
        )}
      </div>

      {canLog && !logging && (
        <div style={{ marginBottom: '10px' }}>
          <button style={s.editBtn} onClick={() => setLogging(true)}>Log a service</button>
        </div>
      )}
      {logging && (
        <div style={{ marginBottom: '12px', border: `1px solid ${C.border}`, borderRadius: '10px', overflow: 'hidden' }}>
          <LogForm mouldId={mould.id} kind={mould.shot_limit_kind} onClose={() => setLogging(false)}
            onDone={() => { setLogging(false); toast.success('Service logged.'); }} />
        </div>
      )}
      <div style={s.titleRow}>
        <div>
          <div style={s.detailRef}>{mould.ref}</div>
          <div style={s.detailTitle}>{mould.name}</div>
          <div style={s.detailSub}>{[mould.customer, mould.part_no].filter(Boolean).join(' · ')}</div>
        </div>
      </div>

      {mould.mould_notes && (
        <div style={s.notesBox}>
          <div style={s.notesTitle}>ℹ Mould Notes</div>
          <div style={s.notesText}>{mould.mould_notes}</div>
        </div>
      )}

      <div style={s.detailGrid}>
        <Section title="Photo">
          <div style={{ position: 'relative' }}>
            {mould.photo_url
              ? <img src={mould.photo_url} alt={mould.ref}
                  style={{ width: '100%', borderRadius: '8px', objectFit: 'cover', maxHeight: '200px' }} />
              : <div style={{ height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4b5563', fontSize: '13px' }}>
                  No photo
                </div>
            }
            {isDirector && (
              <>
                <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoUpload} />
                <button style={{ marginTop: '10px', width: '100%', textAlign: 'center', background: '#1e2329', border: '1px solid #2a3040', color: '#9ca3af', borderRadius: '6px', padding: '6px', fontSize: '12px', cursor: 'pointer' }}
                  onClick={() => fileRef.current.click()} disabled={uploading}>
                  {uploading ? 'Uploading...' : mould.photo_url ? '📷 Change photo' : '📷 Add photo'}
                </button>
              </>
            )}
          </div>
        </Section>

        <Section title="Geometry">
          <Row label="Cavities"        value={mould.cavities} />
          <Row label="Projected area"  value={`${mould.pa_cm2}cm²`} />
          <Row label="Wall max"        value={`${mould.wmax_mm}mm`} />
          <Row label="Wall min"        value={mould.wmin_mm ? `${mould.wmin_mm}mm` : '—'} />
          <Row label="Stack height"    value={mould.stack_mm ? `${mould.stack_mm}mm` : '—'} />
          <Row label="Min opening"     value={mould.min_open_mm ? `${mould.min_open_mm}mm` : '—'} />
        </Section>

        <Section title="Gating">
          <Row label="Gate type"  value={mould.gate_type || '—'} />
          <Row label="Gate dims"  value={gateDesc} />
          <Row label="Gate count" value={mould.gate_count || '—'} />
          <Row label="Runner"     value={mould.runner || '—'} />
          <Row label="Hot runner" value={mould.runner === 'hot' ? 'Yes' : 'No'} />
        </Section>

        {mould.runner === 'hot' && (hrData.controller || hrData.manifold_temp != null || (hrData.zones || []).length > 0) && (
          <Section title="Hot runner">
            <Row label="Nozzle type"   value={hrData.nozzle_type || '—'} />
            <Row label="Controller"    value={hrData.controller || '—'} />
            <Row label="Manifold temp" value={hrData.manifold_temp != null ? `${hrData.manifold_temp}°C` : '—'} />
            {(hrData.zones || []).map((z, i) => (
              <Row key={i} label={z.label || `Zone ${i + 1}`} value={z.temp != null ? `${z.temp}°C` : '—'} />
            ))}
          </Section>
        )}

        <Section title="Shot weights">
          <Row label="Shot weight (all cav + runner)" value={`${mould.sw_g}g`} />
          <Row label="Part weight (per cavity)"       value={`${mould.pw_g}g`} />
          <Row label="Runner weight"                  value={`${mould.rw_g}g`} />
        </Section>

        <Section title="Cooling">
          <Row label="Circuits"           value={mould.circuits || '—'} />
          <Row label="Water dia"          value={mould.water_dia_mm ? `⌀${mould.water_dia_mm}mm` : '—'} />
          {mould.target_mould_temp != null && (
            <Row label="Target mould temp" value={`${mould.target_mould_temp}°C`} />
          )}
        </Section>

        {hasCoolingCalc && (
          <Section title="Cooling calculator">
            {mould.target_mould_temp != null && <>
              <div style={s.calcNote}>Zone temperature targets</div>
              <Row label="Injection half" value={`${mould.target_mould_temp + 3}°C`} />
              <Row label="Ejection half"  value={`${mould.target_mould_temp - 3}°C`} />
              <Row label="Core insert"    value={`${mould.target_mould_temp - 5}°C`} />
            </>}
            {mould.water_dia_mm ? (
              <Row label="Min flow per circuit (Re>4000)"
                value={`${(Q_MIN_FACTOR * mould.water_dia_mm).toFixed(1).replace('.', ',')} L/min`} />
            ) : null}
          </Section>
        )}

        <Section title="Ejection">
          <Row label="Ejector type"   value={mould.ej_type || '—'} />
          <Row label="Ejector count"  value={mould.ej_count || '—'} />
          <Row label="Ejector stroke" value={mould.ej_stroke_mm ? `${mould.ej_stroke_mm}mm` : '—'} />
          <Row label="Ejector pulses" value={mould.ej_pulses || '—'} />
          <Row label="Side actions"   value={mould.side_actions ? 'Yes' : 'No'} />
          <Row label="Lifters"        value={mould.lifters ? 'Yes' : 'No'} />
        </Section>

        {cores.length > 0 && (
          <Section title="Cores & side actions" span>
            {cores.map((core, i) => (
              <div key={i} style={{ marginBottom: i < cores.length - 1 ? '16px' : 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ fontSize: '13px', fontWeight: '700', color: '#f0f2f5' }}>
                    {core.name || `Core ${i + 1}`}
                  </span>
                  <span style={{
                    fontSize: '10px', fontWeight: '700', padding: '2px 6px', borderRadius: '4px',
                    background: core.actuator_type === 'hydraulic' ? '#0c4a6e' : '#14532d',
                    color:      core.actuator_type === 'hydraulic' ? '#7dd3fc' : '#86efac',
                  }}>
                    {core.actuator_type || 'hydraulic'}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '4px', marginBottom: '6px' }}>
                  {core.pressure_bar     != null && <Row label="Pressure"   value={`${core.pressure_bar} bar`} />}
                  {core.stroke_mm        != null && <Row label="Stroke"     value={`${core.stroke_mm}mm`} />}
                  {core.speed_mm_s       != null && <Row label="Speed"      value={`${core.speed_mm_s}mm/s`} />}
                  {core.actuating_time_s != null && <Row label="Actuation"  value={`${core.actuating_time_s}s`} />}
                </div>
                {(core.waiting_times || []).length > 0 && (
                  <div style={{ paddingLeft: '8px', borderLeft: '2px solid #2a3040' }}>
                    <div style={{ ...s.rowLabel, fontSize: '10px', marginBottom: '4px' }}>Waiting times</div>
                    {core.waiting_times.map((wt, wi) => (
                      <Row key={wi}
                        label={TRIGGER_LABELS[wt.trigger] || wt.trigger}
                        value={wt.time_s != null ? `${wt.time_s}s` : '—'} />
                    ))}
                  </div>
                )}
                {i < cores.length - 1 && <div style={{ borderBottom: '1px solid #2a3040', marginTop: '12px' }} />}
              </div>
            ))}
          </Section>
        )}

        {hasSpecMech && (
          <Section title="Special mechanisms">
            {specMech.pneumatic_ejector && <>
              <Row label="Pneumatic ejector valve" value="Yes" />
              {specMech.pneumatic_ejector_notes && (
                <div style={s.mechNote}>{specMech.pneumatic_ejector_notes}</div>
              )}
            </>}
            {specMech.collapsible_cores && <>
              <Row label="Collapsible cores" value="Yes" />
              {specMech.collapsible_cores_notes && (
                <div style={s.mechNote}>{specMech.collapsible_cores_notes}</div>
              )}
            </>}
            {specMech.unscrewing && <>
              <Row label="Unscrewing" value="Yes" />
              {specMech.unscrewing_notes && (
                <div style={s.mechNote}>{specMech.unscrewing_notes}</div>
              )}
            </>}
          </Section>
        )}

        <Section title="Mould info">
          {(mould.mat_families?.length > 0 || mould.mat_family) && (
            <div style={{ ...s.row, alignItems: 'center' }}>
              <span style={s.rowLabel}>Material families</span>
              <span style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', justifyContent: 'flex-end' }}>
                {(mould.mat_families?.length > 0 ? mould.mat_families : [mould.mat_family]).map((m, i) => (
                  <span key={i} style={{ background: '#0f766e', color: '#5eead4', border: '1px solid #0d9488', borderRadius: '4px', padding: '1px 7px', fontSize: '11px', fontWeight: '600' }}>{m}</span>
                ))}
              </span>
            </div>
          )}
          {mould.steel_grades?.length > 0 && (
            <div style={{ ...s.row, alignItems: 'flex-start' }}>
              <span style={s.rowLabel}>Steel grades</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', textAlign: 'right' }}>
                {mould.steel_grades.map((sg, i) => (
                  <div key={i} style={{ fontSize: '12px' }}>
                    {sg.description && <span style={{ color: '#6b7280', marginRight: '6px' }}>{sg.description}</span>}
                    <span style={{ fontWeight: '600', fontFamily: 'monospace' }}>{sg.grade === 'other' ? (sg.custom || 'Other') : sg.grade}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <Row label="Mould base"     value={mould.mould_base === 'other' ? (mould.mould_base_custom || 'Other') : (mould.mould_base || '—')} />
          <Row label="Surface finish"  value={mould.surface_treatment || '—'} />
          <Row label="Toolmaker"     value={mould.toolmaker || '—'} />
          <Row label="Status"        value={mould.status} />
          <Row label="Shots to date" value={mould.shots?.toLocaleString() || '0'} />
        </Section>

        {(mould.toolchange_mount_min || mould.toolchange_unmount_min || mould.additional_startup_min) && (
          <Section title="Scheduling">
            {mould.toolchange_mount_min != null && (
              <Row label="Mount time" value={`${mould.toolchange_mount_min} min`} />
            )}
            {mould.toolchange_unmount_min != null && (
              <Row label="Unmount time" value={`${mould.toolchange_unmount_min} min`} />
            )}
            {mould.additional_startup_min != null && (
              <Row label="Additional startup" value={`${mould.additional_startup_min} min`} />
            )}
          </Section>
        )}

        {isDirector && (mould.tool_cost || mould.shot_life) && (
          <Section title="Financial">
            <Row label="Tool cost"  value={mould.tool_cost  ? `€${parseFloat(mould.tool_cost).toLocaleString()}` : '—'} />
            <Row label="Shot life"  value={mould.shot_life  ? `${parseInt(mould.shot_life).toLocaleString()} shots` : '—'} />
          </Section>
        )}

        {custFields.length > 0 && (
          <Section title="Custom fields">
            {custFields.map((cf, i) => (
              <Row key={i} label={cf.label} value={cf.value} />
            ))}
          </Section>
        )}
      </div>

    </div>
  );
}

// ── SHARED COMPONENTS ─────────────────────────────────────────
function Spec({ label, value }) {
  return (
    <div style={s.spec}>
      <span style={s.specLabel}>{label}</span>
      <span style={s.specValue}>{filled(value)}</span>
    </div>
  );
}

function Section({ title, children, span }) {
  return (
    <div style={{ ...s.section, ...(span ? { gridColumn: '1 / -1' } : {}) }}>
      <div style={s.sectionTitle}>{title}</div>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={s.row}>
      <span style={s.rowLabel}>{label}</span>
      <span style={s.rowValue}>{filled(value)}</span>
    </div>
  );
}

// ── STYLES ────────────────────────────────────────────────────
const s = {
  page:         cs.page,
  header:       cs.pageHeader,
  title:        cs.pageTitle,
  sub:          cs.pageSub,
  addBtn:       cs.btnPrimary,
  importBtn:    cs.btnSecondary,
  grid:         { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' },
  card:         { background: C.surface, border: `1px solid ${C.border}`, borderRadius: '12px', overflow: 'hidden', cursor: 'pointer' },
  cardHeader:   { padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${C.border}` },
  ref:          { fontSize: '12px', fontWeight: '700', color: C.teal, fontFamily: 'monospace' },
  family:       { fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '8px', background: '#1d4ed8', color: '#bfdbfe' },
  cardBody:     { padding: '14px 18px' },
  mouldName:    { fontSize: '14px', fontWeight: '700', marginBottom: '2px' },
  customer:     { fontSize: '11px', color: C.muted, marginBottom: '12px' },
  specs:        { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' },
  spec:         { display: 'flex', flexDirection: 'column', gap: '2px' },
  specLabel:    { fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: C.muted },
  specValue:    { fontSize: '13px', fontWeight: '600', fontFamily: 'monospace', color: C.text },
  cardFooter:   { padding: '10px 18px', borderTop: `1px solid ${C.border}` },
  statusChip:   { fontSize: '10px', fontWeight: '700', padding: '3px 8px', borderRadius: '10px' },
  detailHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  detailActions:{ display: 'flex', gap: '8px' },
  detailRef:    { fontSize: '13px', fontWeight: '700', color: C.teal, fontFamily: 'monospace', marginBottom: '2px' },
  detailTitle:  { fontSize: '24px', fontWeight: '700', marginBottom: '2px' },
  detailSub:    { fontSize: '13px', color: C.muted, marginBottom: '16px' },
  titleRow:     { display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '8px' },
  notesBox:     { background: '#1e3a5f', border: '1px solid #1d4ed8', borderRadius: '10px', padding: '14px 16px', marginBottom: '20px' },
  notesTitle:   { fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#93c5fd', marginBottom: '6px' },
  notesText:    { fontSize: '13px', color: '#bfdbfe', lineHeight: '1.6' },
  detailGrid:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' },
  section:      cs.card,
  sectionTitle: { fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: C.teal, marginBottom: '12px' },
  row:          { display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${C.border}` },
  rowLabel:     { fontSize: '12px', color: C.muted },
  rowValue:     { fontSize: '12px', fontWeight: '600', fontFamily: 'monospace' },
  calcNote:     { fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: C.muted, marginBottom: '4px', marginTop: '4px' },
  mechNote:     { fontSize: '11px', color: '#9ca3af', padding: '4px 0 8px 8px', lineHeight: '1.5' },
  backBtn:      cs.btnSecondary,
  editBtn:      cs.btnSecondary,
  deleteBtn:    cs.btnDanger,
  formField:    { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '10px' },
  formLabel:    cs.label,
  formInput:    cs.input,
  formError:    cs.errorBanner,
  saveBtn:      { ...cs.btnPrimary, padding: '10px 24px' },
  overlay:      cs.overlay,
  modal:        cs.modal,
  modalHeader:  cs.modalHeader,
  modalTitle:   cs.modalTitle,
  closeBtn:     cs.closeBtn,
  modalBody:    cs.modalBody,
  modalFooter:  cs.modalFooter,
  viewToggle:   { display: 'flex', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', overflow: 'hidden' },
  viewBtn:      { background: 'none', border: 'none', color: C.muted, padding: '6px 12px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' },
  viewBtnActive:{ background: C.teal, color: '#fff' },
  center:       { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px', fontFamily: 'system-ui, sans-serif', color: C.muted },
};
